import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, User, Phone, Mail, MapPin, Upload, CreditCard, AlertCircle, AlertTriangle } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';

const HRManagement = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [hrs, setHRs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [idCardPreview, setIdCardPreview] = useState(null);
  const [states, setStates] = useState([]);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [districtSearchInput, setDistrictSearchInput] = useState('');
  const districtInputRef = useRef(null);
  
  // Profile completion state
  const [profileCompleted, setProfileCompleted] = useState(true);
  const [profileMessage, setProfileMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    designation: '',
    state: '',
    district: '',
    id_card: ''
  });

  useEffect(() => {
    fetchHRs();
    fetchStates();
    checkProfileStatus();
  }, []);

  // Check if employer profile is complete
  const checkProfileStatus = async () => {
    try {
      const response = await api.get(`/employer/profile-status/${user.id}`);
      setProfileCompleted(response.data.profile_completed);
      setProfileMessage(response.data.message || '');
    } catch (error) {
      console.error('Error checking profile status:', error);
    }
  };

  // Handle Add HR button click
  const handleAddHR = () => {
    if (!profileCompleted) {
      toast.error('Please complete your company profile before adding HR executives.', {
        description: profileMessage,
        duration: 5000,
        action: {
          label: 'Go to Profile',
          onClick: () => navigate('/employer/profile')
        }
      });
      return;
    }
    setShowDialog(true);
  };

  const fetchHRs = async () => {
    try {
      const response = await api.get(`/employer/hr-executives/${user.id}`);
      setHRs(response.data);
    } catch (error) {
      console.error('Error fetching HRs:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/location-master/states');
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const searchDistricts = async (query) => {
    if (query.length < 2) {
      setDistrictSuggestions([]);
      setShowDistrictSuggestions(false);
      return;
    }
    
    try {
      const response = await api.get(`/location-master/districts/search?q=${encodeURIComponent(query)}&limit=15`);
      setDistrictSuggestions(response.data);
      setShowDistrictSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching districts:', error);
    }
  };

  const handleDistrictSearchChange = (e) => {
    const value = e.target.value;
    setDistrictSearchInput(value);
    searchDistricts(value);
  };

  const selectDistrictSuggestion = (suggestion) => {
    setFormData({
      ...formData,
      state: suggestion.state,
      district: suggestion.district
    });
    setDistrictSearchInput(suggestion.display);
    setDistrictSuggestions([]);
    setShowDistrictSuggestions(false);
  };

  const handleIdCardUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (3MB limit)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('File size must be less than 3MB');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const response = await api.post('/upload/id-proof', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const idCardPath = response.data.path;
      setFormData({ ...formData, id_card: idCardPath });
      const backendUrl = getBackendUrl();
      const fullIdCardUrl = backendUrl + idCardPath;
      setIdCardPreview(fullIdCardUrl);
      toast.success('ID Card uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload ID Card');
    }
  };

  const validateForm = () => {
    if (!formData.name) {
      toast.error('Name is required');
      return false;
    }
    if (!formData.mobile || formData.mobile.length !== 10) {
      toast.error('Valid 10-digit mobile number is required');
      return false;
    }
    if (!formData.email) {
      toast.error('Email is required');
      return false;
    }
    if (!formData.designation) {
      toast.error('Designation is required');
      return false;
    }
    if (!formData.state) {
      toast.error('State is required');
      return false;
    }
    if (!formData.district) {
      toast.error('District is required');
      return false;
    }
    if (!formData.id_card) {
      toast.error('HR Employee ID Card is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      await api.post('/employer/hr-executives', {
        ...formData,
        employer_id: user.id
      });
      toast.success('HR Executive added successfully! They can now login to the portal.');
      setShowDialog(false);
      resetForm();
      fetchHRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add HR Executive');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      designation: '',
      state: '',
      district: '',
      id_card: ''
    });
    setIdCardPreview(null);
    setDistrictSearchInput('');
  };

  const toggleHRStatus = async (hrId, currentStatus) => {
    try {
      await api.put(`/employer/hr-executives/${hrId}/toggle-status`);
      toast.success(`HR Executive ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchHRs();
    } catch (error) {
      toast.error('Failed to update HR status');
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  return (
    <div>
      {/* Profile Completion Alert */}
      {!profileCompleted && (
        <Alert className="mb-6 border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Profile Incomplete:</strong> {profileMessage || 'Please complete your company profile to add HR executives.'}
            <Button 
              variant="link" 
              className="text-amber-700 underline ml-2 p-0 h-auto"
              onClick={() => navigate('/employer/profile')}
            >
              Complete Profile Now →
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>HR Executives</h1>
          <p className="text-sm text-slate-600 mt-1">Manage your HR team members and their portal access</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { 
          if (open && !profileCompleted) { handleAddHR(); return; }
          setShowDialog(open); 
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-hr-btn" 
              className={profileCompleted ? "bg-teal-600 hover:bg-teal-700" : "bg-slate-400 hover:bg-slate-500"}
              onClick={(e) => { if (!profileCompleted) { e.preventDefault(); handleAddHR(); } }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add HR Executive
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add HR Executive</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name <span className="text-red-500">*</span></label>
                <Input
                  data-testid="hr-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Full name of HR Executive"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Mobile <span className="text-red-500">*</span></label>
                <Input
                  data-testid="hr-mobile-input"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  placeholder="10-digit mobile number"
                  required
                  maxLength={10}
                />
                <p className="text-xs text-slate-500 mt-1">This will be used for HR login</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Email <span className="text-red-500">*</span></label>
                <Input
                  data-testid="hr-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Email address"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Designation <span className="text-red-500">*</span></label>
                <Input
                  data-testid="hr-designation-input"
                  value={formData.designation}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                  placeholder="e.g., HR Manager, Recruiter"
                  required
                />
              </div>

              {/* Location Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">State <span className="text-red-500">*</span></label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(value) => setFormData({...formData, state: value, district: ''})}
                  >
                    <SelectTrigger data-testid="hr-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">District <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Input
                      ref={districtInputRef}
                      data-testid="hr-district-input"
                      placeholder="Type to search..."
                      value={districtSearchInput || formData.district}
                      onChange={handleDistrictSearchChange}
                      onFocus={() => {
                        if (districtSearchInput.length >= 2) {
                          setShowDistrictSuggestions(true);
                        }
                      }}
                    />
                    {showDistrictSuggestions && districtSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {districtSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
                            onClick={() => selectDistrictSuggestion(suggestion)}
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              <span>{suggestion.display}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ID Card Upload */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">HR Employee ID Card <span className="text-red-500">*</span></h4>
                    <p className="text-xs text-amber-700 mt-1">
                      Upload HR's Employee ID Card for admin verification and approval.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {idCardPreview && (
                    <img 
                      src={idCardPreview} 
                      alt="ID Card" 
                      className="h-16 w-24 object-cover rounded border border-amber-300"
                    />
                  )}
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border border-amber-400 bg-white rounded-md hover:bg-amber-50 text-amber-700">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{formData.id_card ? 'Change ID Card' : 'Upload ID Card'}</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleIdCardUpload}
                      className="hidden"
                      data-testid="hr-idcard-upload"
                    />
                  </label>
                </div>
                <p className="text-xs text-amber-600 mt-2">JPG, PNG or PDF (Max 3MB)</p>
              </div>

              <Button data-testid="save-hr-btn" type="submit" className="w-full bg-teal-600 hover:bg-teal-700">
                Add HR Executive
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Alert */}
      <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
        <p className="text-sm text-teal-800">
          <strong>Note:</strong> HR Executives are automatically approved and can login immediately using the Employer Login with their mobile number. 
          They will have access to post and manage jobs on behalf of your company. You can deactivate any HR at any time.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {hrs.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg border border-slate-200">
            <User className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No HR Executives added yet</p>
            <p className="text-sm text-slate-500 mt-1">Click "Add HR Executive" to add your first team member</p>
          </div>
        ) : (
          hrs.map((hr) => (
            <Card key={hr.id} className="p-6 border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {hr.id_card ? (
                  <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200 overflow-hidden">
                    <img 
                      src={getImageUrl(hr.id_card)} 
                      alt="ID Card" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200">
                    <CreditCard className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">{hr.name}</h3>
                  <p className="text-sm text-slate-500">{hr.designation || 'HR Executive'}</p>
                </div>
              </div>
              
              <div className="mt-4 space-y-2">
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {hr.mobile}
                </p>
                {hr.email && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {hr.email}
                  </p>
                )}
                {(hr.state || hr.district) && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {hr.district}, {hr.state}
                  </p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={hr.active !== false}
                    onCheckedChange={() => toggleHRStatus(hr.id, hr.active !== false)}
                    data-testid={`toggle-hr-${hr.id}`}
                  />
                  <span className={`text-sm font-medium ${hr.active !== false ? 'text-green-600' : 'text-slate-500'}`}>
                    {hr.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  hr.active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {hr.active !== false ? 'Can Login' : 'Access Disabled'}
                </span>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default HRManagement;
